import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, Link } from 'react-router-dom'
import { reelsApi } from '../../api/reels'
import { useAuth } from '../../context/AuthContext'
import { getStorageUrl } from '../../utils/constants'
import Header from '../../components/layout/Header'
import Footer from '../../components/layout/Footer'
import Loading from '../../components/common/Loading'
import ReactPlayer from 'react-player'
import { FiPlay, FiVolume2, FiVolumeX, FiHeart, FiMoreVertical, FiArrowRight, FiBookOpen, FiTag, FiChevronLeft } from 'react-icons/fi'
import './Reels.css'

const Reels = () => {
  const [playingIndex, setPlayingIndex] = useState(0)
  const [muted, setMuted] = useState(false) // Global mute toggle like TikTok/Shorts
  const [likedReels, setLikedReels] = useState(() => {
    // Load liked reels from localStorage on mount
    try {
      const stored = localStorage.getItem('likedReels')
      return stored ? JSON.parse(stored) : []
    } catch (error) {
      console.error('Error loading liked reels from localStorage:', error)
      return []
    }
  })
  const containerRef = useRef(null)
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()

  const { data, isLoading } = useQuery({
    queryKey: ['reels'],
    queryFn: () => reelsApi.getReels(),
  })

  // Save liked reels to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('likedReels', JSON.stringify(likedReels))
    } catch (error) {
      console.error('Error saving liked reels to localStorage:', error)
    }
  }, [likedReels])

  // Intersection Observer to handle auto-scroll playing
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.6, // Trigger when 60% of the video is visible
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = Number(entry.target.getAttribute('data-index'))
          setPlayingIndex(index)
        }
      })
    }, options)

    const elements = document.querySelectorAll('.reel-section')
    elements.forEach((el) => observer.observe(el))

    return () => {
      if (observer) observer.disconnect()
    }
  }, [data])

  const toggleMute = () => setMuted((prev) => !prev)

  const handlePlayPause = (index) => {
    if (playingIndex === index) {
      setPlayingIndex(null) // Pause
    } else {
      setPlayingIndex(index) // Play
    }
  }

  const handleLike = (e, reel) => {
    e.stopPropagation()
    
    // Check current like state before updating
    const isCurrentlyLiked = likedReels.includes(reel.id)
    
    // Toggle like state locally
    setLikedReels((prev) => {
      if (isCurrentlyLiked) {
        // Unlike: remove from array
        return prev.filter((id) => id !== reel.id)
      } else {
        // Like: add to array
        return [...prev, reel.id]
      }
    })

    // Optionally sync with API in the background (non-blocking)
    if (isAuthenticated) {
      if (isCurrentlyLiked) {
        reelsApi.unlikeReel(reel.id).catch((error) => {
          console.error('Error unliking reel:', error)
        })
      } else {
        reelsApi.likeReel(reel.id).catch((error) => {
          console.error('Error liking reel:', error)
        })
      }
    }
  }

  if (isLoading) {
    return (
      <>
        <Header />
        <Loading fullScreen text="جاري التحميل..." />
        <Footer />
      </>
    )
  }

  const reels = data?.data?.reels || []

  return (
    <div className="reels-page">
      {/* Header and Footer hidden for full-screen reels experience */}
      <div style={{ display: 'none' }}>
        <Header />
      </div>
      
      {/* Back Button */}
      <button 
        className="reels-back-button"
        onClick={() => navigate(-1)}
        aria-label="رجوع"
      >
        <FiArrowRight size={24} />
      </button>
      
      {/* Main Container for Scroll Snap */}
      <main className="shorts-container" ref={containerRef}>
        {reels.length > 0 ? (
          reels.map((reel, index) => {
            const isPlaying = playingIndex === index
            
            return (
              <div 
                key={reel.id} 
                className="reel-section" 
                data-index={index}
              >
                {/* Video Layer */}
                <div className="video-wrapper" onClick={() => handlePlayPause(index)}>
                  <ReactPlayer
                    url={getStorageUrl(reel.url)}
                    width="100%"
                    height="100%"
                    playing={isPlaying}
                    muted={muted}
                    loop
                    playsinline
                    className="react-player-instance"
                    config={{
                      file: { attributes: { style: { objectFit: 'cover', width: '100%', height: '100%' } } }
                    }}
                  />
                </div>

                {/* Dark Gradient Overlay for text readability */}
                <div className="gradient-overlay"></div>

                {/* Right Side Actions (Like, Mute, More) */}
                <div className="shorts-sidebar">
                  <div className="action-item">
                    <button 
                      className={`icon-btn ${likedReels.includes(reel.id) ? 'liked' : ''}`}
                      onClick={(e) => handleLike(e, reel)}
                    >
                      <FiHeart size={28} fill={likedReels.includes(reel.id) ? 'currentColor' : 'none'} />
                    </button>
                    <span className="action-text">
                      {likedReels.includes(reel.id) 
                        ? (reel.likes_count || 0) + 1 
                        : (reel.likes_count || 0)}
                    </span>
                  </div>

                  <div className="action-item">
                    <button className="icon-btn" onClick={toggleMute}>
                      {muted ? <FiVolumeX size={28} /> : <FiVolume2 size={28} />}
                    </button>
                    <span className="action-text">{muted ? 'كتم' : 'صوت'}</span>
                  </div>

                  <div className="action-item">
                    <button className="icon-btn">
                      <FiMoreVertical size={28} />
                    </button>
                  </div>
                </div>

                {/* Bottom Info Area (Title, Description) */}
                <div className="shorts-info">
                  <div className="user-details">
                    <h3 className="username">
                      {reel.university?.name || reel.university_name || '3afer.net'}
                    </h3>
                    <button className="follow-btn">متابعة</button>
                  </div>
                  <p className="description">{reel.title || reel.description || 'مشاهدة ممتعة'}</p>
                  
                  {/* Course or Subject Reference Button */}
                  {(reel.course || reel.subject || reel.course_id || reel.subject_id) && (
                    <div className="reel-reference-button-wrapper">
                      {reel.course ? (
                        <Link 
                          to={`/courses/${reel.course.id}`}
                          className="reel-reference-btn reel-course-btn"
                        >
                          <FiBookOpen size={16} />
                          <span>{reel.course.title}</span>
                          <FiChevronLeft size={18} />
                        </Link>
                      ) : reel.subject ? (
                        <Link 
                          to={`/courses?subject=${reel.subject.id}`}
                          className="reel-reference-btn reel-subject-btn"
                        >
                          <FiTag size={16} />
                          <span>{reel.subject.name}</span>
                          <FiChevronLeft size={18} />
                        </Link>
                      ) : reel.course_id ? (
                        <Link 
                          to={`/courses/${reel.course_id}`}
                          className="reel-reference-btn reel-course-btn"
                        >
                          <FiBookOpen size={16} />
                          <span>عرض الكورس</span>
                          <FiChevronLeft size={18} />
                        </Link>
                      ) : reel.subject_id ? (
                        <Link 
                          to={`/courses?subject=${reel.subject_id}`}
                          className="reel-reference-btn reel-subject-btn"
                        >
                          <FiTag size={16} />
                          <span>عرض المادة</span>
                          <FiChevronLeft size={18} />
                        </Link>
                      ) : null}
                    </div>
                  )}
                  
                  <div className="music-ticker">
                    <div className="music-icon">♫</div>
                    <marquee className="music-name">الصوت الأصلي - {reel.title || 'مقطع ترفيهي'}</marquee>
                  </div>
                </div>

                {/* Play/Pause Overlay Icon (shows briefly) */}
                {!isPlaying && (
                  <div className="pause-indicator" onClick={() => handlePlayPause(index)}>
                    <FiPlay />
                  </div>
                )}
              </div>
            )
          })
        ) : (
          <div className="empty-shorts">
            <h3>لا توجد فيديوهات حالياً</h3>
          </div>
        )}
      </main>
      
      {/* Footer hidden for full-screen reels experience */}
      <div style={{ display: 'none' }}>
        <Footer />
      </div>
    </div>
  )
}

export default Reels